import React from "react";
import { styled } from "@mui/material/styles";
import { Link } from "react-router-dom";
import Typography from "@mui/material/Typography";

import { TypographyH1, TypographyH2 } from "../assets/typographyTheme";

const CustomBold = styled("span")`
  font-family: "Aeonik Bold", "Arial";
`;
const CustomParagraph = styled('p')`
   line-height:22px;
   padding-top: 15px;
`;
const CustomList = styled('ul')(({ theme: { breakpoints } }) => `
  list-style-type: initial;
  margin-left: 20px;
  font-family: 'Aeonik Regular';
  ${[breakpoints.down('sm')]} {
    margin-top: 24px;
    margin-bottom: 30px;
  }
`);
const CustomTable = styled('table')`
    border: 1px solid black;
    border-collapse: collapse;
    & th {
        border: 1px solid black;
    }
    & td {
      border: 1px solid black;
      text-align: center;
    }
`;


const ComposePageRichTextRenderer = ({ jsonData, setShowNavigationAlert, setExternalLink, setExternalLinkModalType }) => {
  const isLinkOutsideModrna = (url) => {
    return !url.includes('modernadirect');
  };
  
  const renderTextWithLineBreaks = (text) => {
    return text.split("\n").map((segment, index) =>
      index === 0 ? segment : [<br key={index} />, segment]
    );
  };
  
  const renderNode = (node) => {
    if (!node) return null;
  
    const { nodeType, content, data } = node;
  
    switch (nodeType) {
      case "document":
        return content.map((child, index) => (
          <React.Fragment key={index}>{renderNode(child)}</React.Fragment>
        ));
  
      case "paragraph":
        return (
          <CustomParagraph>
            {content.map((child, index) => renderNode(child))}
          </CustomParagraph>
        );
  
      case "heading-1":
        return <TypographyH1>{content.map((child, index) => renderNode(child))}</TypographyH1>;
  
      case "heading-2":
        return <TypographyH2>{content.map((child, index) => renderNode(child))}</TypographyH2>;
  
      case "heading-3":
        return <Typography variant="h5" pt="80px">{content.map((child, index) => renderNode(child))}</Typography>;
  
      case "unordered-list":
        return <CustomList>{content.map((child, index) => renderNode(child))}</CustomList>;
  
      case "ordered-list":
        return <ol>{content.map((child, index) => renderNode(child))}</ol>;
  
      case "list-item":
        return <li>{content.map((child, index) => renderNode(child))}</li>;
  
      case "table":
        return <CustomTable>{content.map((child, index) => renderNode(child))}</CustomTable>;
  
      case "embedded-asset-block":
        const { title, file } = data?.target?.fields || {};
        return <img src={file?.url} alt={title} style={{ maxWidth: "100%" }} />;
  
      case "text":
        const isBold = node.marks?.some((mark) => mark.type === "bold");
        const isUnderline = node.marks?.some((mark) => mark.type === "underline");
        const formattedText = renderTextWithLineBreaks(node.value);
  
        let textElement = formattedText;
        if (isBold) textElement = <CustomBold>{textElement}</CustomBold>;
        if (isUnderline) textElement = <u>{textElement}</u>;
  
        return textElement;
  
      case "hyperlink":
        return (
          <Link
            to={{ pathname: data.uri }}
            target={isLinkOutsideModrna(data.uri) ? "_blank" : "_self"}
            rel={isLinkOutsideModrna(data.uri) ? "noopener noreferrer" : ""}
            onClick={(e) => {openExternalLinkHandler(e, data.uri)}}
            data-external-modal={isLinkOutsideModrna(data.uri) ? "dataExternalSocialModal" : "dataExternalModal"}
          >
            {content?.[0]?.value}
          </Link>
        );
      
      case "hr":
        return <hr />;
  
      default:
        return <span>{content?.map((child, index) => renderNode(child))}</span>;
    }
  };
  
  const openExternalLinkHandler = (event, url) => {
    event.preventDefault();
    if (isLinkOutsideModrna(url) && !localStorage.getItem("dontRemindMeExternal")) {
      setShowNavigationAlert(true);
      setExternalLink(url);
      setExternalLinkModalType("extrenalLink");
    } else {
        window.open(url, '_blank');
        setExternalLink("");
      }
  }

  return (
    <div>
      {renderNode(jsonData)}
    </div>
  );
};

export default ComposePageRichTextRenderer;
